import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryClient';
import { supabase } from '@/lib/supabase';

export const Route = createFileRoute('/admin/cost')({
  component: CostTelemetryDashboard,
});

// Cost telemetry dashboard — scaffolded Sprint 0, populated progressively
// across Sprint 1-5 per plan §6.4. The shape is fixed here so that
// downstream sprints can write rows into `cost_telemetry` and have them
// render without touching this component again.

interface CostRow {
  service: string;
  metric: string;
  free_tier_limit: number;
  current_usage: number;
  unit: string;
  period: string;
}

const PLACEHOLDER_ROWS: CostRow[] = [
  {
    service: 'Supabase',
    metric: 'Database size',
    free_tier_limit: 500,
    current_usage: 0,
    unit: 'MB',
    period: 'now',
  },
  {
    service: 'Supabase',
    metric: 'Monthly active users',
    free_tier_limit: 50_000,
    current_usage: 0,
    unit: 'MAU',
    period: 'this month',
  },
  {
    service: 'Supabase',
    metric: 'Egress',
    free_tier_limit: 2_048,
    current_usage: 0,
    unit: 'MB',
    period: 'this month',
  },
  {
    service: 'Cloudflare R2',
    metric: 'Storage',
    free_tier_limit: 10,
    current_usage: 0,
    unit: 'GB',
    period: 'now',
  },
  {
    service: 'Amazon SES',
    metric: 'Emails sent',
    free_tier_limit: 62_000,
    current_usage: 0,
    unit: 'emails',
    period: 'this month',
  },
  {
    service: 'Twilio',
    metric: 'SMS sent',
    free_tier_limit: 0,
    current_usage: 0,
    unit: 'SMS',
    period: 'this month',
  },
  {
    service: 'Stripe',
    metric: 'Transactions',
    free_tier_limit: 0,
    current_usage: 0,
    unit: 'txns',
    period: 'this month',
  },
];

function severityFor(pct: number): { color: string; label: string } {
  if (pct >= 95) return { color: 'var(--color-danger)', label: 'critical' };
  if (pct >= 80) return { color: 'var(--color-warn)', label: 'warn' };
  if (pct >= 60) return { color: 'var(--color-info)', label: 'notice' };
  return { color: 'var(--color-success)', label: 'ok' };
}

function CostTelemetryDashboard() {
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.cost,
    queryFn: async (): Promise<CostRow[]> => {
      // Sprint 10 wires the cost_telemetry_latest view (one row per
      // service+metric, most recent). If the collector cron hasn't run
      // yet (fresh environment), fall back to the zeroed placeholder
      // shape so the dashboard renders end-to-end.
      const { data, error } = await supabase
        .from('cost_telemetry_latest')
        .select('service, metric, current_usage, free_tier_limit, unit, period')
        .order('service', { ascending: true });
      if (error) return PLACEHOLDER_ROWS;
      const rows = (data as CostRow[] | null) ?? [];
      return rows.length > 0 ? rows : PLACEHOLDER_ROWS;
    },
    placeholderData: PLACEHOLDER_ROWS,
  });

  const rows = data ?? PLACEHOLDER_ROWS;

  return (
    <div className="space-y-8">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Operations
        </p>
        <h1 className="mt-2">Monthly cost</h1>
        <p className="mt-2 max-w-prose text-[var(--color-ink-muted)] text-[var(--text-base)]">
          Usage against each service's free tier. Alerts fire at 60 / 80 / 95% per the runbook.
        </p>
      </header>

      <div role="region" aria-label="Cost telemetry table" className="surface overflow-hidden">
        <table className="w-full text-left">
          <caption className="sr-only">Free-tier usage by service</caption>
          <thead className="bg-[var(--color-surface-sunken)] text-[var(--text-sm)]">
            <tr>
              <th scope="col" className="px-4 py-3 font-medium">
                Service
              </th>
              <th scope="col" className="px-4 py-3 font-medium">
                Metric
              </th>
              <th scope="col" className="px-4 py-3 text-right font-medium">
                Usage
              </th>
              <th scope="col" className="px-4 py-3 text-right font-medium">
                Free tier
              </th>
              <th scope="col" className="px-4 py-3 text-right font-medium">
                % used
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading && rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-[var(--color-ink-subtle)]">
                  Loading…
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const pct =
                  row.free_tier_limit > 0 ? (row.current_usage / row.free_tier_limit) * 100 : 0;
                const severity = severityFor(pct);
                return (
                  <tr
                    key={`${row.service}-${row.metric}`}
                    className="border-t border-[var(--color-border)]"
                  >
                    <td className="px-4 py-3 font-medium">{row.service}</td>
                    <td className="px-4 py-3 text-[var(--color-ink-muted)]">{row.metric}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {row.current_usage.toLocaleString()} {row.unit}
                    </td>
                    <td className="px-4 py-3 text-right text-[var(--color-ink-muted)] tabular-nums">
                      {row.free_tier_limit > 0
                        ? `${row.free_tier_limit.toLocaleString()} ${row.unit}`
                        : 'pay-as-you-go'}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      <span
                        className="inline-flex items-center gap-2"
                        aria-label={`${pct.toFixed(0)} percent, ${severity.label}`}
                      >
                        <span
                          className="inline-block h-2 w-2 rounded-full"
                          style={{ background: severity.color }}
                          aria-hidden="true"
                        />
                        {row.free_tier_limit > 0 ? `${pct.toFixed(0)}%` : '—'}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
